export type Role = 'admin' | 'instructor' | 'learner';
export type ProductRole = 'owner' | 'admin' | 'teamLead' | 'educator' | 'salesAssociate' | 'learner';

export interface Tenant {
  tenantId: string; nameLatin: string; nameDev: string;
  tagline: string; hero: string;
  modules: Array<'crm' | 'lms' | 'treasury' | 'attendance' | 'payroll'>;
  roleLabel: Record<string, string>;
}
export const TENANTS: Record<string, Tenant> = {
  techtatwa: {
    tenantId: 'techtatwa', nameLatin: 'tech tatwa', nameDev: 'तत्त्व',
    tagline: 'For course providers · LMS + CRM · multi-tenant',
    hero: 'Run a course platform without the wrangling.',
    modules: ['crm', 'lms', 'treasury', 'attendance', 'payroll'],
    roleLabel: { admin: 'ADMIN', instructor: 'INSTRUCTOR', learner: 'LEARNER' },
  },
  contentcrusaders: {
    tenantId: 'contentcrusaders', nameLatin: 'Content Crusaders', nameDev: '',
    tagline: 'Mumbai · creator-bootcamp guild',
    hero: 'Build creative careers cohort by cohort.',
    modules: ['crm', 'lms', 'treasury', 'payroll'],
    roleLabel: { admin: 'OWNER', instructor: 'MENTOR', learner: 'CREATOR' },
  },
  sproutacademy: {
    tenantId: 'sproutacademy', nameLatin: 'Sprout Academy', nameDev: '',
    tagline: 'Bengaluru · K-12 enrichment',
    hero: 'After-school learning that grows daily.',
    modules: ['crm', 'lms', 'attendance', 'payroll'],
    roleLabel: { admin: 'FOUNDER', instructor: 'TUTOR', learner: 'STUDENT' },
  },
};

export interface NavItem { id: string; label: string; icon: string; badge?: string | number; }
export interface NavSection { section: string; items: NavItem[]; }
export const NAV_BY_ROLE: Record<Role, NavSection[]> = {
  admin: [
    { section: 'Operate', items: [
      { id: 'overview', label: 'Overview', icon: 'over' },
      { id: 'courses', label: 'Courses', icon: 'book' },
      { id: 'course-detail', label: 'Course LMS', icon: 'book', hidden: true },
      { id: 'batches', label: 'Batches', icon: 'batch' },
      { id: 'students', label: 'Students', icon: 'users' },
      { id: 'attendance', label: 'Attendance', icon: 'tick' },
      { id: 'pipeline', label: 'Pipeline', icon: 'pipe' },
      { id: 'leads-inbox', label: 'Leads inbox', icon: 'inbox' },
      { id: 'treasury', label: 'Treasury', icon: 'cash' },
      { id: 'commerce', label: 'Student Commerce', icon: 'cash' },
      { id: 'commerce-finance', label: 'Finance ops', icon: 'cash' },
      { id: 'site-content', label: 'Website admin', icon: 'globe' },
      { id: 'payroll', label: 'Payroll', icon: 'wallet' },
      { id: 'payroll-history', label: 'Pay history', icon: 'history' },
    ]},
    { section: 'Workspace', items: [
      { id: 'members', label: 'Members', icon: 'team' },
      { id: 'staff-attendance', label: 'Staff attendance', icon: 'staff' },
      { id: 'settings', label: 'Settings', icon: 'gear' },
    ]},
  ],
  instructor: [
    { section: 'Teach', items: [
      { id: 'overview', label: 'Today', icon: 'over' },
      { id: 'courses', label: 'My courses', icon: 'book' },
      { id: 'course-detail', label: 'Course LMS', icon: 'book', hidden: true },
      { id: 'batches', label: 'My batches', icon: 'batch' },
      { id: 'students', label: 'Roster', icon: 'users' },
      { id: 'attendance', label: 'Attendance', icon: 'tick' },
      { id: 'payroll', label: 'My payroll', icon: 'wallet' },
      { id: 'commerce', label: 'My commerce', icon: 'cash' },
    ]},
    { section: 'Account', items: [
      { id: 'staff-attendance', label: 'My attendance', icon: 'staff' },
      { id: 'settings', label: 'Settings', icon: 'gear' },
    ]},
  ],
  learner: [
    { section: 'Learn', items: [
      { id: 'overview', label: 'Today', icon: 'over' },
      { id: 'courses', label: 'My courses', icon: 'book' },
      { id: 'course-detail', label: 'Course LMS', icon: 'book', hidden: true },
      { id: 'attendance', label: 'Attendance', icon: 'tick' },
    ]},
    { section: 'Account', items: [
      { id: 'settings', label: 'Settings', icon: 'gear' },
    ]},
  ],
};

export const NAV_BY_ACCESS_ROLE: Record<ProductRole, NavSection[]> = {
  owner: NAV_BY_ROLE.admin,
  admin: NAV_BY_ROLE.admin,
  teamLead: [
    { section: 'Lead operations', items: [
      { id: 'overview', label: 'Today', icon: 'over' },
      { id: 'pipeline', label: 'Pipeline', icon: 'pipe' },
      { id: 'leads-inbox', label: 'Leads inbox', icon: 'inbox' },
      { id: 'students', label: 'Students', icon: 'users' },
      { id: 'batches', label: 'Batches', icon: 'batch' },
    ]},
    { section: 'Account', items: [{ id: 'settings', label: 'Settings', icon: 'gear' }] },
  ],
  educator: NAV_BY_ROLE.instructor,
  salesAssociate: [
    { section: 'Sales', items: [
      { id: 'overview', label: 'Today', icon: 'over' },
      { id: 'leads-inbox', label: 'My leads', icon: 'inbox' },
      { id: 'pipeline', label: 'My pipeline', icon: 'pipe' },
    ]},
    { section: 'Account', items: [{ id: 'settings', label: 'Settings', icon: 'gear' }] },
  ],
  learner: NAV_BY_ROLE.learner,
};

export const ROUTES = ['overview','courses','course-detail','batches','students','attendance','pipeline','treasury','commerce','commerce-finance','site-content','payroll','payroll-history','staff-attendance','members','settings','leads-inbox'] as const;
export type RouteName = typeof ROUTES[number];

export interface PayrollSlip {
  hours: number; otHours: number;
  gross: number; otPay: number; grossTotal: number;
  pf: number; profTax: number; tds: number;
  totalDed: number; net: number;
  tdsApplied: boolean;
  thresholdNote?: string;
}
